import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type Notificacion, type NotificacionesResumen } from "@/lib/api";
import {
  ORDEN_URGENCIA,
  TIPO_META,
  URGENCIA_META,
  metaDeTipo,
  type NivelUrgencia,
} from "@/lib/notifMeta";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaAlertas } from "@/components/ayuda/vistas/alertas";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";

export default function Notificaciones() {
  const [data, setData] = useState<NotificacionesResumen | null>(null);
  const [loading, setLoading] = useState(true);
  // Filtros locales (no van a la URL: es una bandeja de un vistazo, no una
  // vista para compartir/guardar).
  const [soloMios, setSoloMios] = useState(false);
  const [soloNoLeidas, setSoloNoLeidas] = useState(false);
  const [tiposActivos, setTiposActivos] = useState<Set<string>>(new Set());
  const toast = useToast();
  const nav = useNavigate();

  async function refresh() {
    const r = await api.get<NotificacionesResumen>("/api/notificaciones");
    setData(r);
    // Refresca el badge de Alertas en el navbar al instante.
    window.dispatchEvent(new Event("alertas:changed"));
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  async function leerTodas() {
    await api.post("/api/notificaciones/leer-todas");
    await refresh();
    toast.success("Notificaciones marcadas como leídas");
  }

  // Click en la fila: marca la alerta como leida (si no lo estaba) y, si
  // tiene caso asociado, navega directo a ese caso. Es el flujo principal —
  // el usuario llega al caso sin buscarlo y la alerta queda leida sola.
  async function abrirAlerta(n: Notificacion) {
    if (!n.leida_at) {
      try {
        await api.post(`/api/notificaciones/${n.id}/leer`);
        window.dispatchEvent(new Event("alertas:changed"));
      } catch {
        /* si falla el marcado no bloqueamos la navegacion al caso */
      }
    }
    if (n.caso_folio) {
      nav(`/casos/${n.caso_folio}`);
    } else {
      // Alerta sin caso (tipo sistema): solo refrescamos para reflejar
      // que quedo leida.
      await refresh();
    }
  }

  // Marca como leida SIN navegar al caso (boton de la derecha).
  async function marcarLeida(id: number) {
    await api.post(`/api/notificaciones/${id}/leer`);
    await refresh();
  }

  // Marca varias de un jalón (botón "Marcar todas" de un grupo de urgencia).
  async function marcarVarias(ids: number[]) {
    if (!ids.length) return;
    await Promise.all(ids.map((id) => api.post(`/api/notificaciones/${id}/leer`)));
    await refresh();
  }

  function toggleTipo(tipo: string) {
    setTiposActivos((prev) => {
      const next = new Set(prev);
      if (next.has(tipo)) next.delete(tipo);
      else next.add(tipo);
      return next;
    });
  }

  const hayFiltrosActivos = soloMios || soloNoLeidas || tiposActivos.size > 0;

  // Filtra primero (los 3 ejes), luego agrupa por urgencia. Los grupos
  // vacíos tras filtrar no se muestran (menos ruido cuando ya filtraste).
  const grupos = useMemo(() => {
    const items = data?.items ?? [];
    const visibles = items.filter((n) => {
      if (soloMios && !n.es_mio) return false;
      if (soloNoLeidas && n.leida_at) return false;
      if (tiposActivos.size > 0 && !tiposActivos.has(n.tipo)) return false;
      return true;
    });
    // Cualquier urgencia que no esté en URGENCIA_META (valor viejo o nuevo
    // que el front todavía no conoce) cae a "normal" — simétrico al fallback
    // de `tipo` en metaDeTipo(); sin esto, una urgencia desconocida no
    // matcheaba ninguna clave y la notif desaparecía de los 3 grupos en vez
    // de mostrarse en alguno (in-app siempre debe mostrar todo).
    const claveUrgencia = (n: Notificacion): NivelUrgencia =>
      n.urgencia in URGENCIA_META ? (n.urgencia as NivelUrgencia) : "normal";
    return ORDEN_URGENCIA.map((clave) => ({
      clave,
      meta: URGENCIA_META[clave],
      items: visibles.filter((n) => claveUrgencia(n) === clave),
    })).filter((g) => g.items.length > 0);
  }, [data, soloMios, soloNoLeidas, tiposActivos]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-xl font-semibold">Alertas</h1>
            <p className="text-sm text-zinc-400">{data?.total_no_leidas ?? 0} sin leer</p>
          </div>
          <AyudaVista titulo="Alertas" {...ayudaAlertas()} />
        </div>
        <button
          onClick={leerTodas}
          className="btn-ghost w-full sm:w-auto"
          disabled={!data?.total_no_leidas}
        >
          Marcar todas como leídas
        </button>
      </div>

      <div className="card flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Toggle checked={soloMios} onChange={setSoloMios} label="Solo míos" />
          <Toggle checked={soloNoLeidas} onChange={setSoloNoLeidas} label="No leídas" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(TIPO_META).map(([tipo, meta]) => {
            const activo = tiposActivos.has(tipo);
            return (
              <button
                key={tipo}
                type="button"
                onClick={() => toggleTipo(tipo)}
                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition ${
                  activo
                    ? `bg-ink-800 border-current ${meta.color}`
                    : "border-ink-600 text-zinc-500 hover:text-white"
                }`}
              >
                <span aria-hidden>{meta.icono}</span>
                {meta.label}
              </button>
            );
          })}
          {hayFiltrosActivos && (
            <button
              type="button"
              onClick={() => {
                setSoloMios(false);
                setSoloNoLeidas(false);
                setTiposActivos(new Set());
              }}
              className="text-xs px-2.5 py-1 text-zinc-500 hover:text-white transition"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card">
          <p className="text-sm text-zinc-500">Cargando...</p>
        </div>
      ) : !data?.items.length ? (
        <div className="card">
          <p className="text-sm text-zinc-500">Sin notificaciones.</p>
        </div>
      ) : grupos.length === 0 ? (
        <div className="card">
          <p className="text-sm text-zinc-500">Sin alertas con estos filtros.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grupos.map((g) => {
            const noLeidas = g.items.filter((n) => !n.leida_at);
            return (
              <section
                key={g.clave}
                className="rounded-xl border border-ink-700 bg-ink-900 overflow-hidden"
              >
                <div className={`h-1 ${g.meta.punto}`} />
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2 min-w-0">
                      <span className="truncate">{g.meta.titulo}</span>
                      {noLeidas.length > 0 && (
                        <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-accent-500/20 text-accent-300 text-[10px] font-semibold px-1">
                          {noLeidas.length}
                        </span>
                      )}
                    </h2>
                    {noLeidas.length > 0 && (
                      <button
                        type="button"
                        onClick={() => void marcarVarias(noLeidas.map((n) => n.id))}
                        className="flex-shrink-0 text-xs text-accent-400 hover:text-accent-300 transition"
                      >
                        Marcar todas
                      </button>
                    )}
                  </div>
                  <ul className="divide-y divide-ink-700">
                    {g.items.map((n) => {
                      const meta = metaDeTipo(n.tipo);
                      return (
                        <li
                          key={n.id}
                          onClick={() => void abrirAlerta(n)}
                          className={`py-3 px-2 -mx-2 rounded flex items-start gap-3 cursor-pointer transition hover:bg-ink-800/50 ${
                            n.leida_at ? "opacity-50" : ""
                          }`}
                        >
                          <span
                            className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${
                              n.leida_at ? "bg-zinc-600" : "bg-accent-500"
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium min-w-0 break-words">{n.titulo}</p>
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide flex-shrink-0 ${meta.color}`}
                              >
                                <span aria-hidden>{meta.icono}</span>
                                {meta.label}
                              </span>
                            </div>
                            {n.cuerpo && (
                              <p className="text-xs text-zinc-400 mt-0.5 break-words">{n.cuerpo}</p>
                            )}
                            <p className="text-xs text-zinc-600 mt-1">
                              {new Date(n.created_at).toLocaleString()}
                              {n.caso_folio && (
                                <span className="text-accent-500/80"> · abrir caso →</span>
                              )}
                            </p>
                          </div>
                          {n.leida_at ? (
                            <span className="flex-shrink-0 mt-1 text-[10px] uppercase tracking-wide text-zinc-600">
                              Leída
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void marcarLeida(n.id);
                              }}
                              aria-label="Marcar como leída sin abrir el caso"
                              className="flex-shrink-0 mt-0.5 rounded p-1 text-zinc-500 hover:text-accent-300 hover:bg-ink-700 transition"
                            >
                              ✓
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
